import type { DateRangeValue } from "@/components/ui/date-range-picker";
import { lastMonthsRange } from "@/lib/date-utils";
import type {
  PaymentMethod,
  PaymentStatus,
} from "@/services/admin-payment.service";
import { createTableFiltersStore } from "./createTableFiltersStore";

/**
 * Sentinel for "no status / no method filter".
 *
 * Radix Select throws on a `<SelectItem value="">` and treats `""` as "nothing selected", so the
 * cleared state needs a real value. No PaymentStatus or PaymentMethod member is `'All'`, so this
 * cannot collide with a genuine one.
 */
export const ALL = "All";

/**
 * Statuses offered by the filter. Every PaymentStatus member is here — unlike the tenant list,
 * nothing in this enum is unreachable, so leaving one out would hide rows an operator can see in
 * the table.
 */
export const PAYMENT_STATUS_OPTIONS: {
  value: PaymentStatus | typeof ALL;
  label: string;
}[] = [
  { value: ALL, label: "All Statuses" },
  { value: "AwaitingVerification", label: "Awaiting Verification" },
  { value: "Completed", label: "Completed" },
  { value: "Pending", label: "Pending" },
  { value: "Failed", label: "Failed" },
  { value: "Cancelled", label: "Cancelled" },
  { value: "Refunded", label: "Refunded" },
];

/**
 * Methods offered by the filter. Trial is included because trial settlements are real payment rows
 * in this list — the summary cards above it count them (only their money is excluded), so an
 * operator seeing a Trial row needs a way to narrow to it.
 */
export const PAYMENT_METHOD_OPTIONS: {
  value: PaymentMethod | typeof ALL;
  label: string;
}[] = [
  { value: ALL, label: "All Methods" },
  { value: "BankTransfer", label: "Bank Transfer" },
  { value: "QRCode", label: "QR Code" },
  { value: "Trial", label: "Trial" },
  { value: "Balance", label: "Account Balance" },
];

export interface PaymentListFiltersState extends Record<string, unknown> {
  /**
   * The one free-text term. The backend routes it by SHAPE, so this single box covers what used
   * to need two: a GUID matches the payment, tenant or order id; a WIC-PAY- / WIC-ORD- number
   * matches by equality; an e-mail matches the owner exactly; a phone-shaped term matches the
   * owner's number in either stored spelling; anything else is a substring scan of the payment
   * number and owner e-mail. See PaymentRepository.
   */
  search: string;

  status: PaymentStatus | typeof ALL;

  paymentMethod: PaymentMethod | typeof ALL;

  /** Created-at range, ISO strings. Sent as fromDate / toDate. */
  dateRange: DateRangeValue;
}

/**
 * Both ends of the created-at range default to the last six months rather than to "no filter": the
 * list opens on a bounded window, and the operator widens or clears it from the picker. Computed
 * once per page load, so a tab left open for days keeps the window it started with until the
 * filters are reset.
 */
/**
 * True when a stored range has exactly one end. The picker only ever produces both or neither, so
 * this can only come from hand-edited storage.
 */
const isHalfRange = (range: DateRangeValue | undefined): boolean =>
  Boolean(range?.from) !== Boolean(range?.to);

const defaults: PaymentListFiltersState = {
  search: "",
  status: ALL,
  paymentMethod: ALL,
  dateRange: lastMonthsRange(),
};

/**
 * Filter and paging state for the admin payment list, cached in sessionStorage.
 *
 * No `defaultSort`: GetPaymentsQuery has no sort parameters and the repository always orders by
 * creation date descending, so a sort control here would move an arrow without moving the rows.
 *
 * Bump `version` in step with any change to the shape above or to the option lists — a cached value
 * the backend no longer accepts comes back as a 400 on a filter the operator cannot see.
 *
 * v3 retired `tenantId` — the one search box now matches a pasted tenant id by shape, so the
 * separate input is gone.
 *
 * The bump follows the project rule for retiring a persisted filter (frontend-conventions.md). It
 * is belt-and-braces rather than the only guard: `createTableFiltersStore.restoreFilters` rebuilds
 * the filter set from `defaults`, so a key removed from the shape is already dropped on load and
 * cannot go on filtering invisibly. What the bump actually buys is that the whole v2 entry is
 * discarded rather than partly carried forward — at the cost of resetting that operator's cached
 * filters and page size once.
 */
export const usePaymentFiltersStore =
  createTableFiltersStore<PaymentListFiltersState>({
    name: "admin.payments.filters",
    version: 3,
    defaults,
    sanitize: (filters) => ({
      ...filters,

      // sessionStorage outlives a release: a status or method that is no longer offered must not
      // survive as a filter the operator cannot see in the UI, and so cannot clear from it. The
      // endpoint answers 400 for a value outside its enum, so this would break the screen outright.
      status: PAYMENT_STATUS_OPTIONS.some(
        (option) => option.value === filters.status,
      )
        ? filters.status
        : defaults.status,

      paymentMethod: PAYMENT_METHOD_OPTIONS.some(
        (option) => option.value === filters.paymentMethod,
      )
        ? filters.paymentMethod
        : defaults.paymentMethod,

      // Exactly one end is not a range: the query applies createdFrom/createdTo together, so a
      // stored half would show as a filter chip that narrows nothing. An *empty* range is left alone —
      // it is the operator having deliberately cleared the six-month default to search all time, and
      // replacing it here would silently put the default back on every reload.
      dateRange: isHalfRange(filters.dateRange)
        ? defaults.dateRange
        : filters.dateRange,
    }),
  });
